import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronLeft, X, Sparkles } from 'lucide-react';

const TOUR_STEPS = [
  {
    id: 'textarea',
    selector: '[data-testid="journal-textarea"]',
    title: 'Journal',
    description: "This is your space. Write your thoughts, capture memories, and let your stories unfold.",
    page: '/',
  },
  {
    id: 'audio',
    selector: '[data-testid="audio-options-button"]',
    title: 'Voice Record',
    description: "Hit record and talk about your day. NOYO transcribes it automatically.",
    page: '/',
  },
  {
    id: 'images',
    selector: '[data-testid="attach-images-button"]',
    title: 'Attach Memories',
    description: "Add photos to capture the moment. They're stored safely with your entry.",
    page: '/',
  },
  {
    id: 'stories',
    selector: '[data-testid="stories-page-title"]',
    title: 'Stories Extract Themselves',
    description: "NOYO's AI reads your entries and pulls out meaningful stories - sorted into your folders automatically.",
    page: '/stories',
  },
  {
    id: 'story-count',
    selector: '[data-testid="total-story-count-box"]',
    title: 'Watch Your Stories Grow',
    description: "Every journal entry adds to your collection. Watch the count rise as your life story unfolds.",
    page: '/stories',
  },
  {
    id: 'timeline',
    selector: '[data-testid="timeline-page-title"]',
    title: 'Time Travel',
    description: "Mention any date and NOYO remembers. Your life, organized by time.",
    page: '/timeline',
  },
];

function SpotlightTour({ isOpen, onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
  const [showFinalScreen, setShowFinalScreen] = useState(false);
  const containerRef = useRef(null);
  const navigate = useNavigate();

  // Find element by selector
  const findElement = (selector) => {
    const selectors = selector.split(', ');
    for (const sel of selectors) {
      try {
        const el = document.querySelector(sel);
        if (el) return el;
      } catch {
        continue;
      }
    }
    return null;
  };

  // Navigate to the correct page for the current step
  useEffect(() => {
    if (!isOpen || showFinalScreen) return;
    
    const step = TOUR_STEPS[currentStep];
    if (step && step.page && window.location.pathname !== step.page) {
      navigate(step.page);
    }
  }, [isOpen, currentStep, showFinalScreen, navigate]);

  // Update spotlight position
  useEffect(() => {
    if (!isOpen || showFinalScreen) return;

    const updatePosition = () => {
      const step = TOUR_STEPS[currentStep];
      if (!step) return;

      const element = findElement(step.selector);
      if (element) {
        const rect = element.getBoundingClientRect();
        const padding = 8;

        setTargetRect({
          top: rect.top - padding,
          left: rect.left - padding,
          width: rect.width + padding * 2,
          height: rect.height + padding * 2,
        });

        // Calculate tooltip position - ALWAYS below the element
        const tooltipWidth = 320;
        const margin = 16;

        let top = rect.bottom + margin;
        let left = rect.left + rect.width / 2 - tooltipWidth / 2;

        // Keep within viewport horizontally
        left = Math.max(margin, Math.min(left, window.innerWidth - tooltipWidth - margin));
        
        // If tooltip would go off bottom of screen, position it higher but still below element
        const tooltipHeight = 180;
        if (top + tooltipHeight > window.innerHeight - margin) {
          top = window.innerHeight - tooltipHeight - margin;
        }

        setTooltipPos({ top, left, width: tooltipWidth });
      } else {
        // Element not found - center the tooltip
        setTargetRect(null);
        setTooltipPos({
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 320,
        });
      }
    };

    // Initial position with delay for page navigation
    const timer = setTimeout(updatePosition, 300);

    // Update on resize/scroll
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen, currentStep, showFinalScreen]);

  // Manage body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setCurrentStep(0);
      setShowFinalScreen(false);
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const goNext = () => {
    if (currentStep < TOUR_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Show final screen instead of completing immediately
      setShowFinalScreen(true);
    }
  };

  const goPrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const completeTour = () => {
    localStorage.setItem('noyo_tour_completed', 'true');
    navigate('/'); // Go back to home
    onComplete();
  };

  if (!isOpen) return null;

  const step = TOUR_STEPS[currentStep];
  const isLast = currentStep === TOUR_STEPS.length - 1;

  // Final welcome screen
  if (showFinalScreen) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center">
        <div className="absolute inset-0 bg-black/50" />
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="relative z-10 bg-white dark:bg-zinc-900 rounded-3xl shadow-2xl border border-zinc-200 dark:border-zinc-700 p-8 max-w-sm mx-4 text-center"
        >
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
            <Sparkles className="w-8 h-8 text-primary" />
          </div>
          <h2 className="text-2xl font-serif font-semibold text-zinc-900 dark:text-white mb-3">
            You're all set!
          </h2>
          <p className="text-zinc-700 dark:text-zinc-200 mb-6 leading-relaxed">
            Start capturing your story. Every thought, every memory — NOYO keeps them safe.
          </p>
          <button
            onClick={completeTour}
            className="w-full bg-primary text-white rounded-xl py-3 font-medium hover:bg-primary/90 transition-colors"
          >
            Begin Using NOYO
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="fixed inset-0 z-[100]">
      {/* Dark overlay with cutout - 50% opacity */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none">
        <defs>
          <mask id="spotlight-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {targetRect && (
              <rect
                x={targetRect.left}
                y={targetRect.top}
                width={targetRect.width}
                height={targetRect.height}
                rx="12"
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(0, 0, 0, 0.50)"
          mask="url(#spotlight-mask)"
          className="pointer-events-auto"
        />
      </svg>

      {/* Highlight ring */}
      {targetRect && (
        <div
          className="absolute pointer-events-none border-[3px] border-primary rounded-2xl"
          style={{
            top: targetRect.top - 4,
            left: targetRect.left - 4,
            width: targetRect.width + 8,
            height: targetRect.height + 8,
            boxShadow: '0 0 20px 8px hsl(var(--primary) / 0.4)',
          }}
        />
      )}

      {/* Skip button */}
      <button
        onClick={completeTour}
        className="absolute top-4 right-4 z-[120] text-white/70 hover:text-white flex items-center gap-1 text-sm bg-black/40 px-3 py-1.5 rounded-full"
      >
        Skip <X className="w-4 h-4" />
      </button>

      {/* Tooltip - always below, solid background for readability */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
          className="absolute z-[120] bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl border border-zinc-200 dark:border-zinc-700 p-5"
          style={tooltipPos}
        >
          {/* Progress */}
          <div className="flex gap-1.5 mb-4 justify-center">
            {TOUR_STEPS.map((_, i) => (
              <div
                key={i}
                className={`h-1.5 rounded-full transition-all ${
                  i === currentStep ? 'w-6 bg-primary' : i < currentStep ? 'w-1.5 bg-primary/50' : 'w-1.5 bg-zinc-300 dark:bg-zinc-600'
                }`}
              />
            ))}
          </div>

          {/* Content */}
          <h3 className="text-lg font-serif font-semibold text-zinc-900 dark:text-white mb-2 text-center">
            {step.title}
          </h3>
          <p className="text-sm text-zinc-700 dark:text-zinc-200 text-center leading-relaxed mb-4">
            {step.description}
          </p>

          {/* Nav */}
          <div className="flex items-center justify-between">
            <button
              onClick={goPrev}
              disabled={currentStep === 0}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm ${
                currentStep === 0 ? 'text-zinc-400 dark:text-zinc-500 cursor-not-allowed' : 'text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800'
              }`}
            >
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
            <span className="text-xs text-zinc-500 dark:text-zinc-400">{currentStep + 1} / {TOUR_STEPS.length}</span>
            <button
              onClick={goNext}
              className="flex items-center gap-1 px-4 py-1.5 rounded-lg bg-primary text-white hover:bg-primary/90 text-sm font-medium"
            >
              {isLast ? 'Finish' : 'Next'} {!isLast && <ChevronRight className="w-4 h-4" />}
            </button>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

export default SpotlightTour;
